import { type PlatformContract, type PublishPayload, safeWebUrl } from "@ondit/publishing";
import { Button, NativeSelect } from "@ondit/ui";
import { useState } from "react";
import { pick, useI18n, useT } from "~/core/i18n";
import { Hint } from "~/core/ui/page";
import { Markdown } from "~/modules/analytics/components/markdown";
import "~/modules/analytics/features/platform-publishing/publishing.css";

export function PlatformPreview({
	platform,
	payload,
	externalHeading = false,
	resolvedMedia,
	resolvedFiles,
}: {
	platform: PlatformContract;
	payload: PublishPayload;
	externalHeading?: boolean;
	resolvedFiles?: Record<string, string>;
	resolvedMedia?: { url: string; alt: string; kind: "image" | "video" }[];
}) {
	const t = useT();
	const [device, setDevice] = useState("mobile");
	const [slide, setSlide] = useState(0);
	const [expandedBody, setExpandedBody] = useState(false);
	const { locale } = useI18n();
	const media =
		resolvedMedia ??
		payload.media.map((m) => ({ ...m, kind: /\.(mp4|webm|mov)(?:[?#]|$)/i.test(m.url) ? "video" : "image" }));
	const carousel = ["gallery", "pin", "social", "feed"].includes(platform.preview.layout);
	const visible = carousel
		? media.slice(Math.min(slide, Math.max(0, media.length - 1)), Math.min(slide, Math.max(0, media.length - 1)) + 1)
		: media;
	const display = platform.preview.fields.filter(
		(f) => !["body", "title", "destination"].includes(f.key) && payload.fields[f.key],
	);
	return (
		<section
			className={`platform-preview platform-preview--${platform.preview.layout}`}
			data-platform={platform.id}
			data-device={device}
			aria-label={`${platform.name} ${t("publishing.platform.preview")}`}
		>
			<header className="record-heading">
				<strong>{platform.name}</strong>
				<NativeSelect
					aria-label={pick(locale, ["Preview device", "预览设备"])}
					value={device}
					onChange={(e) => setDevice(e.target.value)}
				>
					<option value="mobile">{pick(locale, ["Mobile", "移动端"])}</option>
					<option value="desktop">{pick(locale, ["Desktop", "桌面端"])}</option>
				</NativeSelect>
				{!externalHeading && (
					<span>
						{t("publishing.platform.preview")} <Hint text={t("publishing.platform.previewHelp")} />
					</span>
				)}
			</header>
			{payload.fields.destination && <p className="hint">{payload.fields.destination}</p>}

			{platform.preview.media && (platform.preview.media.required || media.length > 0) && (
				<div className="platform-preview-media">
					{media.length ? (
						visible.map((m) => (
							<figure key={m.url}>
								{(resolvedMedia || safeWebUrl(m.url)) &&
									(m.kind === "video" ? (
										<video
											muted
											src={m.url}
											controls
											preload="metadata"
											aria-label={m.alt || t("publishing.platform.assets")}
										/>
									) : (
										<img src={m.url} alt={m.alt} loading="lazy" referrerPolicy="no-referrer" />
									))}
								<a href={m.url} target="_blank" rel="noreferrer">
									{m.alt || pick(locale, ["Open media", "打开素材"])}
								</a>
							</figure>
						))
					) : (
						<p className="hint">{pick(locale, ["Media preview", "媒体预览"])}</p>
					)}
				</div>
			)}
			{payload.fields.title && <h3>{payload.fields.title}</h3>}
			{carousel && media.length > 1 && (
				<div className="platform-actions">
					<Button
						aria-label={pick(locale, ["Previous image", "上一张"])}
						disabled={slide === 0}
						onClick={() => setSlide((n) => Math.max(0, n - 1))}
					>
						‹
					</Button>
					<span>
						{Math.min(slide + 1, media.length)} / {media.length}
					</span>
					<Button
						aria-label={pick(locale, ["Next image", "下一张"])}
						disabled={slide >= media.length - 1}
						onClick={() => setSlide((n) => Math.min(media.length - 1, n + 1))}
					>
						›
					</Button>
				</div>
			)}
			<div className="platform-preview-body">
				{["article", "newsletter", "discussion"].includes(platform.preview.layout) ? (
					<PublishingBody text={payload.fields.body ?? ""} files={resolvedFiles ?? {}} />
				) : (
					<>
						<p
							className={`platform-plain-text${!expandedBody && (payload.fields.body?.length ?? 0) > 280 ? " platform-preview-excerpt" : ""}`}
						>
							{payload.fields.body}
						</p>
						{(payload.fields.body?.length ?? 0) > 280 && (
							<Button variant="ghost" aria-expanded={expandedBody} onClick={() => setExpandedBody(!expandedBody)}>
								{pick(locale, expandedBody ? ["Show less", "收起"] : ["Read more", "展开全文"])}
							</Button>
						)}
					</>
				)}
			</div>
			{display.length > 0 && (
				<dl className="platform-preview-settings">
					{display.map((f) => (
						<div key={f.key}>
							<dt>{pick(locale, f.label)}</dt>
							<dd>{pick(locale, f.option_labels?.[payload.fields[f.key]]) ?? payload.fields[f.key]}</dd>
						</div>
					))}
				</dl>
			)}
		</section>
	);
}

function PublishingBody({ text, files }: { text: string; files: Record<string, string> }) {
	const sections = [];
	let cursor = 0;
	for (const match of text.matchAll(/^!\[([^\]]*)\]\(([^)\s]+)\)$/gm)) {
		const at = match.index ?? 0;
		if ((text.slice(0, at).match(/^```/gm)?.length ?? 0) % 2 !== 0) continue;
		const src = match[2].startsWith("ondit-file:")
			? files[match[2].slice(11)]
			: safeWebUrl(match[2])
				? match[2]
				: undefined;
		if (!src) continue;
		if (at > cursor) sections.push(<Markdown key={`text-${cursor}`} text={text.slice(cursor, at)} />);
		sections.push(
			<figure key={`image-${at}`}>
				<img src={src} alt={match[1]} loading="lazy" referrerPolicy="no-referrer" />
			</figure>,
		);
		cursor = at + match[0].length;
	}
	if (cursor < text.length) sections.push(<Markdown key={`text-${cursor}`} text={text.slice(cursor)} />);
	return <>{sections}</>;
}
